import { supabase } from '../lib/supabase';
import { getDifficultyConfig } from './lumoPersonalityService';

// Skill tier to difficulty mapping (kept for backward compatibility)
const SKILL_DIFFICULTY_MAP = {
  beginner: { min: 1, max: 2 },
  intermediate: { min: 2, max: 3 },
  advanced: { min: 3, max: 5 }
};

export const objectionsService = {
  /**
   * Get objections filtered by user's industry and skill tier with personality-based difficulty
   * @param {string} userId - Current user's ID
   * @returns {Promise<Array>} - Filtered objections with category info
   */
  async getFilteredObjections(userId, tenantId = null) {
    if (!userId) throw new Error('User ID is required');

    // Step 1: Get user profile
    const { data: profile, error: profileError } = await supabase
      ?.from('user_profiles')
      ?.select('industry, skill_level')
      ?.eq('id', userId)
      ?.single();

    if (profileError) throw profileError;
    if (!profile) throw new Error('User profile not found');

    let { industry, skill_level } = profile;
    let isWhiteLabelTenant = false;
    
    // Step 1.5: If tenant is specified, use tenant slug as industry
    if (tenantId) {
      const { data: tenant } = await supabase
        ?.from('tenants')
        ?.select('slug, id')
        ?.eq('id', tenantId)
        ?.single();
      
      if (tenant?.slug && tenant.slug !== 'default') {
        industry = tenant.slug;
        isWhiteLabelTenant = true;
      }
    }
    
    // Step 2: Get difficulty range using personality service (enhanced filtering)
    const difficultyConfig = getDifficultyConfig(skill_level);
    const [minDiff, maxDiff] = difficultyConfig.difficultyRange;

    // Step 3: Fetch objections with category name join and personality-based difficulty
    let query = supabase
      ?.from('objections')
      ?.select(`
        *,
        objection_categories!inner (
          name
        )
      `)
      ?.or(`industry.eq.${industry},industry.eq.global`)
      ?.gte('difficulty', minDiff)
      ?.lte('difficulty', maxDiff)
      ?.order('difficulty', { ascending: true });

    // Filter by tenant: white-label tenants see their own tenant_id, default tenant sees NULL (no tenant_id)
    if (isWhiteLabelTenant) {
      query = query?.eq('tenant_id', tenantId);
    } else {
      query = query?.is('tenant_id', null);
    }
    
    // Filter objection types based on skill level
    if (difficultyConfig.objectionTypes !== 'all') {
      // For beginners, only show basic objection types (filter by label)
      query = query?.in('label', difficultyConfig.objectionTypes);
    }

    const { data: objections, error: objectionsError } = await query;

    if (objectionsError) throw objectionsError;

    // Format the response to include category name
    return objections?.map(obj => ({
      ...obj,
      category_name: obj?.objection_categories?.name || 'Uncategorized'
    }));
  },

  /**
   * Get user profile information
   * @param {string} userId - Current user's ID
   * @returns {Promise<Object>} - User profile with industry and skill_level
   */
  async getUserProfile(userId) {
    if (!userId) throw new Error('User ID is required');

    const { data, error } = await supabase?.from('user_profiles')?.select('industry, skill_level, full_name')?.eq('id', userId)?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Update user's skill tier
   * @param {string} userId - Current user's ID
   * @param {string} skillLevel - New skill level (beginner/intermediate/advanced)
   * @returns {Promise<Object>} - Updated profile
   */
  async updateSkillTier(userId, skillLevel) {
    if (!userId) throw new Error('User ID is required');
    if (!['beginner', 'intermediate', 'advanced']?.includes(skillLevel)) {
      throw new Error('Invalid skill level. Must be beginner, intermediate, or advanced');
    }

    const { data, error } = await supabase?.from('user_profiles')?.update({ skill_level: skillLevel })?.eq('id', userId)?.select('industry, skill_level, full_name')?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Get all objections (for admin or unrestricted view)
   * @returns {Promise<Array>} - All objections
   */
  async getAllObjections(tenantId = null) {
    let query = supabase?.from('objections')?.select('*')?.order('difficulty', { ascending: true });
    if (tenantId) {
      query = query?.eq('tenant_id', tenantId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  },

  /**
   * Get practice attempts for a user
   * @param {string} userId - Current user's ID
   * @param {string} objectionId - Optional objection ID to filter
   * @returns {Promise<Array>} - User's practice attempts
   */
  async getUserAttempts(userId, objectionId = null) {
    if (!userId) throw new Error('User ID is required');

    let query = supabase?.from('user_objection_attempts')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (objectionId) {
      query = query?.eq('objection_id', objectionId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data;
  },

  /**
   * Create a practice attempt
   * @param {Object} attemptData - Practice attempt data
   * @returns {Promise<Object>} - Created attempt
   */
  async createAttempt(attemptData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase?.from('user_objection_attempts')?.insert({
        user_id: user?.id,
        objection_id: attemptData?.objectionId,
        user_response_text: attemptData?.userResponseText,
        lumo_score: attemptData?.lumoScore,
        lumo_feedback_text: attemptData?.lumoFeedbackText,
        lumo_suggested_script: attemptData?.lumoSuggestedScript
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  /**
   * Calculate user stats based on practice attempts
   * @param {string} userId - Current user's ID
   * @returns {Promise<Object>} - { totalAttempts, successRate, masteredObjections, bookmarked }
   */
  async getUserStats(userId) {
    if (!userId) throw new Error('User ID is required');

    try {
      // Get all attempts
      const { data: attempts, error: attemptsError } = await supabase
        ?.from('user_objection_attempts')
        ?.select('objection_id, lumo_score')
        ?.eq('user_id', userId);

      if (attemptsError) throw attemptsError;

      const totalAttempts = attempts?.length || 0;

      if (totalAttempts === 0) {
        return {
          totalAttempts: 0,
          successRate: 0,
          masteredObjections: 0,
          bookmarked: 0
        };
      }

      // Calculate success rate (attempts with score >= 70)
      const successfulAttempts = attempts?.filter(a => a?.lumo_score >= 70)?.length || 0;
      const successRate = Math.round((successfulAttempts / totalAttempts) * 100);

      // Calculate mastered objections (avg score >= 85 on last 3 attempts per objection)
      const objectionGroups = {};
      attempts?.forEach(attempt => {
        if (!objectionGroups[attempt?.objection_id]) {
          objectionGroups[attempt?.objection_id] = [];
        }
        objectionGroups[attempt?.objection_id]?.push(attempt?.lumo_score);
      });

      let masteredObjections = 0;
      Object.values(objectionGroups)?.forEach(scores => {
        const recentScores = scores?.slice(0, 3);
        const avgScore = recentScores?.reduce((a, b) => a + b, 0) / recentScores?.length;
        if (avgScore >= 85) {
          masteredObjections++;
        }
      });

      return {
        totalAttempts,
        successRate,
        masteredObjections,
        bookmarked: 0 // Placeholder; can be implemented when bookmarks table is added
      };
    } catch (error) {
      console.error('Error calculating user stats:', error);
      return {
        totalAttempts: 0,
        successRate: 0,
        masteredObjections: 0,
        bookmarked: 0
      };
    }
  }
};